import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {MutationObserver | null} */
  #mainPriceObserver = null;

  /** @type {boolean} */
  #priceSyncScheduled = false;

  /**
   * 商品オプション（自前アプリ）が最後に出した価格。
   * @type {{productId?: string|number, base: number, options: number, total: number, clamped: boolean, formatted: {base: string, options: string, total: string}} | null}
   */
  #optionPrice = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener('product-options:update', this.#handleOptionPriceUpdate, { signal });

    this.#getInitialQuantity();
    this.#setupPriceMirror();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#mainPriceObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    const footer = document.querySelector('footer');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';

    // `.click()` ではなく cancelable な click を自前で投げる。
    // 商品オプションの必須チェック（product-options.js）は window の capture で
    // preventDefault するので、戻り値 false でそれを検知できる。
    // `.click()` だと止められたかどうかが分からず、下の fly-to-cart が
    // カートに入っていないのに再生されてしまう。
    const added = this.#targetAddToCartButton.dispatchEvent(
      new MouseEvent('click', { bubbles: true, cancelable: true })
    );
    if (!added) {
      delete this.#targetAddToCartButton.dataset.puppet;
      return;
    }

    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    this.#optionPrice = null;

    // A native variant change re-renders the main price element, so rebind the
    // mirror observer and re-sync the option-adjusted price into the sticky bar.
    this.#setupPriceMirror();

    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * 商品オプションの選択が変わったときの価格更新。
   * `blocks/product-options.liquid` の component が実数を投げてくる。
   * @param {CustomEvent} event
   */
  #handleOptionPriceUpdate = (event) => {
    const detail = event.detail;
    if (!detail) return;
    if (detail.productId && String(detail.productId) !== this.dataset.productId) return;

    this.#optionPrice = detail;
    this.#applyOptionBreakdown();
  };

  // Price breakdown (商品オプション対応)
  /**
   * Locates the main (non-sticky) product price element for this product.
   * @returns {HTMLElement | null}
   */
  #getMainPriceElement() {
    const section = this.closest('.shopify-section');
    const productId = this.dataset.productId;
    if (!section || !productId) return null;

    const candidates = section.querySelectorAll(`product-price[data-product-id="${productId}"]`);
    for (const el of candidates) {
      // Skip the price rendered inside the sticky bar itself.
      if (!el.closest('sticky-add-to-cart')) return /** @type {HTMLElement} */ (el);
    }
    return null;
  }

  /** 主価格の実額と会員価格だけを同期し、追従バーの行構造は維持する。 */
  #syncStickyPrice = () => {
    const main = this.#getMainPriceElement();
    const breakdown = this.querySelector('.sticky-price');
    if (!breakdown) return;

    if (main) {
      const selling = main.querySelector('.price[data-price]');
      const member = main.querySelector('.member-price[data-price]');
      if (selling) {
        breakdown.dataset.basePrice = selling.dataset.price;
        this.querySelector('[ref="priceBase"]').textContent = selling.textContent;
        this.querySelector('[ref="priceTotal"]').textContent = selling.textContent;
      }
      // 会員ランク不明など、主価格で非表示なら追従バーにも出さない。
      breakdown.dataset.memberBasePrice = member?.dataset.price || '';
      breakdown.dataset.memberBaseFormatted = member?.textContent || '';
      this.querySelector('[ref="priceMember"]').textContent = member?.textContent || '';
      this.querySelector('[ref="priceMemberRow"]').hidden = !member;

      const shipping = main.querySelector('.price-shipping-fee');
      const target = this.querySelector('[ref="priceShipping"]');
      target.replaceChildren(...(shipping ? [shipping.cloneNode(true)] : []));
      this.querySelector('[ref="priceShippingRow"]').hidden = !shipping;
    }

    this.#applyOptionBreakdown();
  };

  /**
   * この商品のオプションコンポーネントを探す。
   * イベントより先に追従バーが初期化されることがあるので、初期表示はこちらから拾う。
   * @returns {HTMLElement | null}
   */
  #getOptionsComponent() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const scope = this.closest('.shopify-section') || document;
    const candidates = scope.querySelectorAll('product-options-component');
    for (const el of candidates) {
      // data-product-id を持たない古い描画はそのまま採用する（1PDP=1商品なので実害なし）
      const root = el.querySelector('[data-product-id]');
      if (!root || root.getAttribute('data-product-id') === productId) {
        return /** @type {HTMLElement} */ (el);
      }
    }
    return null;
  }

  /**
   * 本体・オプション・合計・会員価格を別々の行に表示する。
   * 会員価格は既存の本体会員価格にオプション実額を加算する。
   * 下限価格に張り付く場合は、内訳と会員価格の推測表示を避ける。
   */
  #applyOptionBreakdown() {
    const breakdown = this.querySelector('.sticky-price');
    if (!breakdown) return;

    const optionsComponent = /** @type {any} */ (this.#getOptionsComponent());
    const state = this.#optionPrice ?? optionsComponent?.priceState ?? null;
    if (!state) return;

    this.querySelector('[ref="priceBase"]').textContent = state.formatted.base;
    this.querySelector('[ref="priceOptions"]').textContent =
      state.formatted.options || `+${optionsComponent?.formatMoney?.(0) || '¥0'}`;
    this.querySelector('[ref="priceTotal"]').textContent = state.formatted.total;
    this.querySelector('[ref="priceBaseRow"]').hidden = state.clamped;
    this.querySelector('[ref="priceOptionsRow"]').hidden = state.clamped;

    const memberBase = Number(breakdown.dataset.memberBasePrice);
    const memberRow = this.querySelector('[ref="priceMemberRow"]');
    const canFormat = typeof optionsComponent?.formatMoney === 'function';
    memberRow.hidden = !memberBase || state.clamped || (!canFormat && state.options !== 0);
    if (!memberRow.hidden) {
      this.querySelector('[ref="priceMember"]').textContent = canFormat
        ? optionsComponent.formatMoney(Math.max(0, memberBase + state.options))
        : breakdown.dataset.memberBaseFormatted;
    }
  }

  /**
   * Coalesces rapid mutations into a single sync on the next frame.
   */
  #schedulePriceSync = () => {
    if (this.#priceSyncScheduled) return;
    this.#priceSyncScheduled = true;
    requestAnimationFrame(() => {
      this.#priceSyncScheduled = false;
      this.#syncStickyPrice();
    });
  };

  /**
   * (Re)establishes the MutationObserver that mirrors the main price into the
   * sticky bar. Observes the price element's parent so that node replacement,
   * not just text changes, is also caught.
   */
  #setupPriceMirror() {
    this.#mainPriceObserver?.disconnect();

    const main = this.#getMainPriceElement();
    const observeTarget = main?.parentElement || main;
    if (!observeTarget) return;

    this.#mainPriceObserver = new MutationObserver(this.#schedulePriceSync);
    this.#mainPriceObserver.observe(observeTarget, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // Initial sync for any option value already applied on load.
    this.#syncStickyPrice();
  }

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
